const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const API_URL = `${API_BASE_URL}/api`;

class ApiService {
  constructor() {
    this.baseURL = API_URL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Remove Content-Type for FormData
    if (options.body instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'API request failed');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Analytics APIs
  async getDashboardAnalytics() {
    return this.request('/analytics/dashboard');
  }

  async getDetailedAnalytics(range = '7d') {
    return this.request(`/analytics/detailed?range=${range}`);
  }

  async getPerformanceMetrics(range = '30d') {
    return this.request(`/analytics/performance?range=${range}`);
  }

  async getConversionFunnel(range = '30d') {
    return this.request(`/analytics/conversion-funnel?range=${range}`);
  }

  async getRealtimeAnalytics() {
    return this.request('/analytics/realtime');
  }

  async getAnalytics(timeRange) {
    return this.request(`/analytics/detailed?range=${timeRange}`);
  }

  async getRealTimeAnalytics() {
    return this.request('/analytics/realtime');
  }

  async trackEvent(eventData) {
    return this.request('/analytics/track', {
      method: 'POST',
      body: JSON.stringify(eventData),
    });
  }

  // Subscribers APIs
  async getSubscribers(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/subscribers?${query}`);
  }

  async getSubscriberStats() {
    return this.request('/subscribers/stats');
  }

  async createSubscriber(data) {
    return this.request('/subscribers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSubscriber(id, data) {
    return this.request(`/subscribers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSubscriber(id) {
    return this.request(`/subscribers/${id}`, {
      method: 'DELETE',
    });
  }

  async bulkSubscriberOperation(data) {
    return this.request('/subscribers/bulk', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async exportSubscribers(params = {}) {
    const query = new URLSearchParams(params).toString();
    const response = await fetch(`${this.baseURL}/subscribers/export?${query}`);
    return response.blob();
  }

  // Contact/Resume APIs
  async getContacts(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/contact?${query}`);
  }

  async getContactStats() {
    return this.request('/contact/stats');
  }

  async getContact(id) {
    return this.request(`/contact/${id}`);
  }

  async updateContactStatus(id, status) {
    return this.request(`/contact/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async deleteContact(id) {
    return this.request(`/contact/${id}`, {
      method: 'DELETE',
    });
  }

  // Content APIs
  async getContent(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/content?${query}`);
  }

  async getContentStats() {
    return this.request('/content/stats');
  }

  async getContentItem(id) {
    return this.request(`/content/${id}`);
  }

  async createContent(data) {
    return this.request('/content', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateContent(id, data) {
    return this.request(`/content/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteContent(id) {
    return this.request(`/content/${id}`, {
      method: 'DELETE',
    });
  }

  async publishContent(id) {
    return this.request(`/content/${id}/publish`, {
      method: 'POST',
    });
  }

  async duplicateContent(id) {
    return this.request(`/content/${id}/duplicate`, {
      method: 'POST',
    });
  }

  async sendNewsletter(id, data) {
    return this.request(`/content/${id}/send-newsletter`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Public APIs (for frontend)
  async getBlogPosts(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/content/public/blog?${query}`);
  }

  async getBlogPost(slug) {
    return this.request(`/content/public/blog/${slug}`);
  }

  // Health check
  async getHealth() {
    return this.request('/health');
  }

  // Analytics Export & Management
  async exportAnalytics(format = 'json') {
    const endpoint = `/analytics/export?format=${format}`;
    
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'analytics-export';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      } else {
        filename += format === 'csv' ? '.csv' : '.json';
      }
      
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      return { status: 'success', message: 'Export completed successfully' };
    } catch (error) {
      console.error('Export error:', error);
      throw error;
    }
  }

  async resetAnalytics() {
    return this.request('/analytics/reset', {
      method: 'POST'
    });
  }

  async cleanupOldAnalytics() {
    return this.request('/analytics/cleanup', {
      method: 'POST'
    });
  }

  // Client Intake Forms APIs
  async getClientIntakeForms(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/clientIntake?${query}`);
  }

  async getClientIntakeStats() {
    return this.request('/clientIntake/stats');
  }

  async getClientIntakeForm(id) {
    return this.request(`/clientIntake/${id}`);
  }

  async updateClientIntakeStatus(id, data) {
    return this.request(`/clientIntake/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteClientIntakeForm(id) {
    return this.request(`/clientIntake/${id}`, {
      method: 'DELETE',
    });
  }

  async bulkClientIntakeOperation(data) {
    return this.request('/clientIntake/bulk', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async exportClientIntakeForms(params = {}) {
    const query = new URLSearchParams(params).toString();
    const response = await fetch(`${this.baseURL}/clientIntake/export?${query}`);
    return response.blob();
  }
}

// Create and export singleton instance
const apiService = new ApiService();
export default apiService;

// Export specific methods for convenience
export const {
  getDashboardAnalytics,
  getDetailedAnalytics,
  getPerformanceMetrics,
  getConversionFunnel,
  getRealtimeAnalytics,
  getSubscribers,
  getSubscriberStats,
  createSubscriber,
  updateSubscriber,
  deleteSubscriber,
  bulkSubscriberOperation,
  exportSubscribers,
  getContacts,
  getContactStats,
  getContact,
  updateContactStatus,
  deleteContact,
  getContent,
  getContentStats,
  getContentItem,
  createContent,
  updateContent,
  deleteContent,
  publishContent,
  duplicateContent,
  sendNewsletter,
  getBlogPosts,
  getBlogPost,
  getHealth,
  exportAnalytics,
  resetAnalytics,
  cleanupOldAnalytics,
  getClientIntakeForms,
  getClientIntakeStats,
  getClientIntakeForm,
  updateClientIntakeStatus,
  deleteClientIntakeForm,
  bulkClientIntakeOperation,
  exportClientIntakeForms
} = apiService; 